import React, { useMemo, useState } from "react";
import { SectionCard } from "./patientChartShared";

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  background: "#fff",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "grid",
  gap: 6,
  fontSize: 14,
  color: "#334155",
};

const buttonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#0f172a",
  color: "#fff",
  borderColor: "#0f172a",
};

const dangerButtonStyle = {
  ...buttonStyle,
  color: "#b91c1c",
  borderColor: "#fecaca",
};

const blankMedication = () => ({
  medication: "",
  dose: "",
  route: "",
  frequency: "",
  quantity: "",
  instructions: "",
});

function getProviderName(provider = {}) {
  return (
    provider.displayName ||
    provider.name ||
    provider.fullName ||
    provider.email ||
    ""
  );
}

function disabledButtonStyle(baseStyle, disabled) {
  return {
    ...baseStyle,
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : baseStyle.cursor || "pointer",
  };
}

export default function RXSection({
  selectedEncounterId,
  patient,
  encounter,
  providers = [],
  expandedSections,
  toggleSection,
  sectionRef,
  activeSection,
  onGenerateRx,
  isGeneratingRx = false,
  readOnlyMode = false,
}) {
  const [providerUid, setProviderUid] = useState("");
  const [providerPin, setProviderPin] = useState("");
  const [medications, setMedications] = useState([blankMedication()]);

  const selectedProvider = useMemo(
    () => providers.find((p) => String(p.uid || p.id) === String(providerUid)),
    [providers, providerUid]
  );

  const controlsDisabled = readOnlyMode || isGeneratingRx;

  function setMedicationField(index, field, value) {
    if (readOnlyMode) return;

    setMedications((prev) =>
      prev.map((med, i) => (i === index ? { ...med, [field]: value } : med))
    );
  }

  function addMedication() {
    if (readOnlyMode) return;
    setMedications((prev) => [...prev, blankMedication()]);
  }

  function removeMedication(index) {
    if (readOnlyMode) return;

    setMedications((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );
  }

  function handleGenerate() {
    if (readOnlyMode) return;

    if (!selectedEncounterId) {
      alert("Create or select an encounter first.");
      return;
    }

    if (!providerUid) {
      alert("Select a prescribing provider.");
      return;
    }

    if (!providerPin.trim()) {
      alert("Enter the provider PIN.");
      return;
    }

    const usableMeds = medications.filter(
      (m) =>
        m.medication.trim() ||
        m.dose.trim() ||
        m.route.trim() ||
        m.frequency.trim() ||
        m.quantity.trim() ||
        m.instructions.trim()
    );

    if (!usableMeds.length) {
      alert("Enter at least one medication.");
      return;
    }

    onGenerateRx({
      providerUid,
      providerPin,
      medications: usableMeds,
    });
  }

  return (
    <SectionCard
      title="RX / Prescription"
      sectionKey="rx"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={sectionRef}
      activeSection={activeSection}
      extraHeaderContent={
        selectedEncounterId ? (
          <button
            type="button"
            style={disabledButtonStyle(
              primaryButtonStyle,
              isGeneratingRx || readOnlyMode
            )}
            onClick={handleGenerate}
            disabled={isGeneratingRx || readOnlyMode}
          >
            {isGeneratingRx ? "Generating RX..." : "Generate RX"}
          </button>
        ) : null
      }
    >
      {!selectedEncounterId ? (
        <div style={{ color: "#64748b" }}>
          Create or select an encounter first.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 12,
              background: "#f8fafc",
              display: "grid",
              gap: 6,
            }}
          >
            <div style={{ fontWeight: 800 }}>Patient</div>
            <div>{patient?.fullName || "—"}</div>
            <div style={{ color: "#64748b", fontSize: 13 }}>
              DOB: {patient?.dob || "—"} • Age: {patient?.age || "—"} •
              Gender: {patient?.gender || "—"}
            </div>
            <div style={{ color: "#64748b", fontSize: 13 }}>
              Allergies:{" "}
              {patient?.allergiesNone ? "None" : patient?.allergies || "—"}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            <label style={labelStyle}>
              Prescribing Provider
              <select
                style={inputStyle}
                value={providerUid}
                onChange={(e) => {
                  if (readOnlyMode) return;
                  setProviderUid(e.target.value);
                }}
                disabled={controlsDisabled}
              >
                <option value="">Select provider</option>
                {providers.map((provider) => {
                  const uid = provider.uid || provider.id;
                  return (
                    <option key={uid} value={uid}>
                      {getProviderName(provider)}
                    </option>
                  );
                })}
              </select>
            </label>

            <label style={labelStyle}>
              Provider PIN
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name={`rx-pin-${Date.now()}`}
                maxLength={8}
                style={inputStyle}
                value={providerPin || ""}
                onChange={(e) => {
                  if (readOnlyMode) return;
                  setProviderPin(e.target.value.replace(/\D/g, ""));
                }}
                placeholder="Enter PIN"
                disabled={controlsDisabled}
              />
            </label>
          </div>

          {selectedProvider && (
            <div style={{ fontSize: 13, color: "#475569" }}>
              Selected: <strong>{getProviderName(selectedProvider)}</strong>
            </div>
          )}

          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ fontWeight: 800 }}>Medication(s)</div>

            {medications.map((med, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: 12,
                  display: "grid",
                  gap: 10,
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 10,
                  }}
                >
                  <label style={labelStyle}>
                    Medication
                    <input
                      style={inputStyle}
                      value={med.medication || ""}
                      onChange={(e) =>
                        setMedicationField(index, "medication", e.target.value)
                      }
                      disabled={controlsDisabled}
                    />
                  </label>

                  <label style={labelStyle}>
                    Dose
                    <input
                      style={inputStyle}
                      value={med.dose || ""}
                      onChange={(e) =>
                        setMedicationField(index, "dose", e.target.value)
                      }
                      disabled={controlsDisabled}
                    />
                  </label>

                  <label style={labelStyle}>
                    Route
                    <input
                      style={inputStyle}
                      value={med.route || ""}
                      onChange={(e) =>
                        setMedicationField(index, "route", e.target.value)
                      }
                      disabled={controlsDisabled}
                    />
                  </label>

                  <label style={labelStyle}>
                    Frequency
                    <input
                      style={inputStyle}
                      value={med.frequency || ""}
                      onChange={(e) =>
                        setMedicationField(index, "frequency", e.target.value)
                      }
                      disabled={controlsDisabled}
                    />
                  </label>

                  <label style={labelStyle}>
                    Quantity
                    <input
                      style={inputStyle}
                      value={med.quantity || ""}
                      onChange={(e) =>
                        setMedicationField(index, "quantity", e.target.value)
                      }
                      disabled={controlsDisabled}
                    />
                  </label>
                </div>

                <label style={labelStyle}>
                  Instructions
                  <textarea
                    style={{
                      ...inputStyle,
                      minHeight: 70,
                      resize: "vertical",
                    }}
                    value={med.instructions || ""}
                    onChange={(e) =>
                      setMedicationField(index, "instructions", e.target.value)
                    }
                    disabled={controlsDisabled}
                  />
                </label>

                <div>
                  <button
                    type="button"
                    style={disabledButtonStyle(
                      dangerButtonStyle,
                      controlsDisabled || medications.length === 1
                    )}
                    onClick={() => removeMedication(index)}
                    disabled={controlsDisabled || medications.length === 1}
                  >
                    Remove Medication
                  </button>
                </div>
              </div>
            ))}

            <div>
              <button
                type="button"
                style={disabledButtonStyle(buttonStyle, controlsDisabled)}
                onClick={addMedication}
                disabled={controlsDisabled}
              >
                + Add Medication
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}